import {
  patientWorkspaceDocumentCommandContractVersion,
  patientWorkspaceDocumentCommandRoutePath,
  patientWorkspaceDocumentCompleteUploadContractVersion,
  patientWorkspaceDocumentCompleteUploadRoutePath,
  patientWorkspaceDocumentDownloadRoutePath,
  patientWorkspaceDocumentUploadIntentContractVersion,
  patientWorkspaceDocumentUploadIntentRoutePath,
  patientWorkspaceMessageCommandContractVersion,
  patientWorkspaceMessageCommandRoutePath,
  patientWorkspaceOnboardingCommandContractVersion,
  patientWorkspaceOnboardingCommandRoutePath,
  patientWorkspaceSpeechContractVersion,
  patientWorkspaceSpeechRoutePath,
  patientWorkspaceSummaryContractVersion,
  patientWorkspaceSummaryRoutePath,
  supportSubmissionContractVersion,
  supportSubmissionRoutePath,
  type PatientWorkspaceDocumentCommandRequestV1,
  type PatientWorkspaceDocumentCommandResponseV1,
  type PatientWorkspaceDocumentCompleteUploadRequestV1,
  type PatientWorkspaceDocumentCompleteUploadResponseV1,
  type PatientWorkspaceDocumentUploadIntentRequestV1,
  type PatientWorkspaceDocumentUploadIntentResponseV1,
  type PatientWorkspaceMessageCommandRequestV1,
  type PatientWorkspaceMessageCommandResponseV1,
  type PatientWorkspaceOnboardingCommandRequestV1,
  type PatientWorkspaceOnboardingCommandResponseV1,
  type PatientWorkspaceSpeechEngineV1,
  type PatientWorkspaceSpeechRequestV1,
  type PatientWorkspaceSpeechVoiceIdV1,
  type PatientWorkspaceSummaryResponseV1,
  type SupportSubmissionRequestV1,
  type SupportSubmissionResponseV1,
} from "@amplecare/contracts";

import type { MobileRuntimeConfig } from "../config/runtime-config.js";
import {
  prepareBrowserDocumentUploadPayload,
  uploadPreparedDocumentBytes,
  type BrowserDocumentUploadFile,
} from "./document-upload-client.js";
import {
  isPatientWorkspaceScaffoldMode,
  loadPatientWorkspaceScaffoldSummary,
  recordPatientWorkspaceScaffoldDocument,
  recordPatientWorkspaceScaffoldMessage,
  recordPatientWorkspaceScaffoldOnboarding,
  submitPatientWorkspaceScaffoldSupportSubmission,
  synthesizePatientWorkspaceScaffoldSpeech,
} from "./patient-workspace-scaffold.js";

function readBrowserOrigin(): string | null {
  if (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    window.location.origin.length > 0
  ) {
    return window.location.origin;
  }

  return null;
}

function isLoopbackOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);

    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
  } catch {
    return false;
  }
}

function resolveSessionFetchBaseUrls(config: MobileRuntimeConfig): string[] {
  const browserOrigin = readBrowserOrigin();
  const prefersBrowserOrigin =
    typeof browserOrigin === "string" &&
    browserOrigin.length > 0 &&
    !isLoopbackOrigin(browserOrigin);
  const baseUrls = (
    prefersBrowserOrigin
      ? [browserOrigin, config.sessionApiBaseUrl]
      : [config.sessionApiBaseUrl, browserOrigin]
  ).filter((value): value is string => typeof value === "string" && value.length > 0);

  return [...new Set(baseUrls)];
}

function resolveSessionFetchBaseUrl(config: MobileRuntimeConfig): string {
  const [baseUrl] = resolveSessionFetchBaseUrls(config);

  if (baseUrl) {
    return baseUrl;
  }

  throw new Error("patient-workspace-session-base-url-unavailable");
}

function buildPatientWorkspaceSummaryUrl(
  baseUrl: string,
  tenantId: string,
  siteId: string | null,
  patientId: string | null,
): string {
  const url = new URL(patientWorkspaceSummaryRoutePath, baseUrl);

  url.searchParams.set("tenantId", tenantId);

  if (siteId) {
    url.searchParams.set("siteId", siteId);
  }

  if (patientId) {
    url.searchParams.set("patientId", patientId);
  }

  return url.toString();
}

function buildPatientWorkspaceDocumentCommandUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    patientWorkspaceDocumentCommandRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildPatientWorkspaceDocumentDownloadUrl(
  config: MobileRuntimeConfig,
  input: {
    documentId: string;
    patientId: string | null;
  },
): string {
  const url = new URL(
    patientWorkspaceDocumentDownloadRoutePath,
    resolveSessionFetchBaseUrl(config),
  );

  url.searchParams.set("tenantId", config.requestTenantId);

  if (config.requestSiteId) {
    url.searchParams.set("siteId", config.requestSiteId);
  }

  if (input.patientId) {
    url.searchParams.set("patientId", input.patientId);
  }

  url.searchParams.set("documentId", input.documentId);

  return url.toString();
}

function buildPatientWorkspaceDocumentUploadIntentUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    patientWorkspaceDocumentUploadIntentRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildPatientWorkspaceDocumentCompleteUploadUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    patientWorkspaceDocumentCompleteUploadRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildPatientWorkspaceMessageCommandUrl(
  config: MobileRuntimeConfig,
): string {
  return new URL(
    patientWorkspaceMessageCommandRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildPatientWorkspaceOnboardingCommandUrl(
  baseUrl: string,
): string {
  return new URL(patientWorkspaceOnboardingCommandRoutePath, baseUrl).toString();
}

function buildPatientWorkspaceSpeechUrl(config: MobileRuntimeConfig): string {
  return new URL(
    patientWorkspaceSpeechRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

function buildSupportSubmissionUrl(config: MobileRuntimeConfig): string {
  return new URL(
    supportSubmissionRoutePath,
    resolveSessionFetchBaseUrl(config),
  ).toString();
}

export async function loadPatientWorkspaceSummary(
  config: MobileRuntimeConfig,
  patientId: string | null,
  signal?: AbortSignal,
): Promise<PatientWorkspaceSummaryResponseV1> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return loadPatientWorkspaceScaffoldSummary(config, patientId);
  }

  const baseUrls = resolveSessionFetchBaseUrls(config);

  if (baseUrls.length === 0) {
    throw new Error("patient-workspace-session-base-url-unavailable");
  }

  let lastError: unknown = null;

  for (const baseUrl of baseUrls) {
    try {
      const response = await fetch(
        buildPatientWorkspaceSummaryUrl(
          baseUrl,
          config.requestTenantId,
          config.requestSiteId,
          patientId,
        ),
        {
          credentials: "include",
          headers: {
            accept: "application/json",
          },
          method: "GET",
          signal,
        },
      );

      if (!response.ok) {
        throw new Error(`patient-workspace-summary-failed-${response.status}`);
      }

      const payload =
        (await response.json()) as PatientWorkspaceSummaryResponseV1;

      if (
        payload.schemaVersion !== patientWorkspaceSummaryContractVersion ||
        payload.tenantId !== config.requestTenantId
      ) {
        throw new Error("patient-workspace-summary-schema-mismatch");
      }

      return payload;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("patient-workspace-summary-failed");
}

export async function recordPatientWorkspaceDocument(
  config: MobileRuntimeConfig,
  input: {
    patientId: string | null;
    label: string;
    summaryText: string;
    createdByLabel: string;
  },
  signal?: AbortSignal,
): Promise<PatientWorkspaceDocumentCommandResponseV1> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return recordPatientWorkspaceScaffoldDocument(config, input);
  }

  const request: PatientWorkspaceDocumentCommandRequestV1 = {
    schemaVersion: patientWorkspaceDocumentCommandContractVersion,
    tenantId: config.requestTenantId,
    siteId: config.requestSiteId,
    patientId: input.patientId,
    label: input.label,
    summaryText: input.summaryText,
    createdByLabel: input.createdByLabel,
  };
  const response = await fetch(
    buildPatientWorkspaceDocumentCommandUrl(config),
    {
      body: JSON.stringify(request),
      credentials: "include",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      method: "POST",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `patient-workspace-document-command-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as PatientWorkspaceDocumentCommandResponseV1;

  if (
    payload.schemaVersion !== patientWorkspaceDocumentCommandContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("patient-workspace-document-command-schema-mismatch");
  }

  return payload;
}

export function openPatientWorkspaceDocumentDownload(
  config: MobileRuntimeConfig,
  input: {
    documentId: string;
    patientId: string | null;
  },
): boolean {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return false;
  }

  if (typeof document === "undefined" || typeof window === "undefined") {
    return false;
  }

  const anchor = document.createElement("a");

  anchor.href = buildPatientWorkspaceDocumentDownloadUrl(config, input);
  anchor.rel = "noopener,noreferrer";
  anchor.target = "_blank";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  return true;
}

export async function uploadPatientWorkspaceDocument(
  config: MobileRuntimeConfig,
  input: {
    documentTypeKey?: string | null;
    file: BrowserDocumentUploadFile;
    patientId: string | null;
    title?: string | null;
  },
  signal?: AbortSignal,
): Promise<PatientWorkspaceDocumentCompleteUploadResponseV1> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    throw new Error("patient-workspace-document-upload-scaffold-unavailable");
  }

  const preparedUpload = await prepareBrowserDocumentUploadPayload(input.file);
  const title = input.title?.trim() || preparedUpload.title;
  const uploadIntentRequest: PatientWorkspaceDocumentUploadIntentRequestV1 = {
    schemaVersion: patientWorkspaceDocumentUploadIntentContractVersion,
    tenantId: config.requestTenantId,
    siteId: config.requestSiteId,
    patientId: input.patientId,
    filename: preparedUpload.filename,
    mediaType: preparedUpload.mediaType,
    byteLength: preparedUpload.byteLength,
    sha256: preparedUpload.sha256Hex,
    title,
    documentTypeKey: input.documentTypeKey ?? null,
  };
  const intentResponse = await fetch(
    buildPatientWorkspaceDocumentUploadIntentUrl(config),
    {
      body: JSON.stringify(uploadIntentRequest),
      credentials: "include",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      method: "POST",
      signal,
    },
  );

  if (!intentResponse.ok) {
    throw new Error(
      `patient-workspace-document-upload-intent-failed-${intentResponse.status}`,
    );
  }

  const intentPayload =
    (await intentResponse.json()) as PatientWorkspaceDocumentUploadIntentResponseV1;

  if (
    intentPayload.schemaVersion !==
      patientWorkspaceDocumentUploadIntentContractVersion ||
    intentPayload.tenantId !== config.requestTenantId ||
    intentPayload.uploadMethod !== "PUT"
  ) {
    throw new Error("patient-workspace-document-upload-intent-schema-mismatch");
  }

  if (preparedUpload.byteLength > intentPayload.maxByteLength) {
    throw new Error("patient-workspace-document-upload-too-large");
  }

  await uploadPreparedDocumentBytes(
    {
      bytes: preparedUpload.bytes,
      uploadHeaders: intentPayload.uploadHeaders,
      uploadUrl: intentPayload.uploadUrl,
    },
    signal,
  );

  const completeUploadRequest: PatientWorkspaceDocumentCompleteUploadRequestV1 =
    {
      schemaVersion: patientWorkspaceDocumentCompleteUploadContractVersion,
      tenantId: config.requestTenantId,
      siteId: config.requestSiteId,
      patientId: input.patientId,
      uploadId: intentPayload.uploadId,
      documentId: intentPayload.documentId,
      byteLength: preparedUpload.byteLength,
      sha256: preparedUpload.sha256Hex,
      title,
      documentTypeKey: input.documentTypeKey ?? null,
    };
  const completeResponse = await fetch(
    buildPatientWorkspaceDocumentCompleteUploadUrl(config),
    {
      body: JSON.stringify(completeUploadRequest),
      credentials: "include",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      method: "POST",
      signal,
    },
  );

  if (!completeResponse.ok) {
    throw new Error(
      `patient-workspace-document-complete-upload-failed-${completeResponse.status}`,
    );
  }

  const completePayload =
    (await completeResponse.json()) as PatientWorkspaceDocumentCompleteUploadResponseV1;

  if (
    completePayload.schemaVersion !==
      patientWorkspaceDocumentCompleteUploadContractVersion ||
    completePayload.tenantId !== config.requestTenantId
  ) {
    throw new Error(
      "patient-workspace-document-complete-upload-schema-mismatch",
    );
  }

  return completePayload;
}

export async function recordPatientWorkspaceMessage(
  config: MobileRuntimeConfig,
  input: {
    patientId: string | null;
    bodyText: string;
  },
  signal?: AbortSignal,
): Promise<PatientWorkspaceMessageCommandResponseV1> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return recordPatientWorkspaceScaffoldMessage(config, input);
  }

  const request: PatientWorkspaceMessageCommandRequestV1 = {
    schemaVersion: patientWorkspaceMessageCommandContractVersion,
    tenantId: config.requestTenantId,
    siteId: config.requestSiteId,
    patientId: input.patientId,
    bodyText: input.bodyText,
  };
  const response = await fetch(buildPatientWorkspaceMessageCommandUrl(config), {
    body: JSON.stringify(request),
    credentials: "include",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    method: "POST",
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `patient-workspace-message-command-failed-${response.status}`,
    );
  }

  const payload =
    (await response.json()) as PatientWorkspaceMessageCommandResponseV1;

  if (
    payload.schemaVersion !== patientWorkspaceMessageCommandContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("patient-workspace-message-command-schema-mismatch");
  }

  return payload;
}

export async function recordPatientWorkspaceOnboarding(
  config: MobileRuntimeConfig,
  input: Omit<
    PatientWorkspaceOnboardingCommandRequestV1,
    "schemaVersion" | "tenantId"
  >,
  signal?: AbortSignal,
): Promise<PatientWorkspaceOnboardingCommandResponseV1> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return recordPatientWorkspaceScaffoldOnboarding(config, input);
  }

  const request: PatientWorkspaceOnboardingCommandRequestV1 = {
    schemaVersion: patientWorkspaceOnboardingCommandContractVersion,
    tenantId: config.requestTenantId,
    ...input,
    siteId: config.requestSiteId,
  };
  const requestBody = JSON.stringify(request);
  const baseUrls = resolveSessionFetchBaseUrls(config);

  if (baseUrls.length === 0) {
    throw new Error("patient-workspace-session-base-url-unavailable");
  }

  let lastError: unknown = null;

  for (const baseUrl of baseUrls) {
    try {
      const response = await fetch(
        buildPatientWorkspaceOnboardingCommandUrl(baseUrl),
        {
          body: requestBody,
          credentials: "include",
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
          method: "POST",
          signal,
        },
      );

      if (!response.ok) {
        throw new Error(
          `patient-workspace-onboarding-command-failed-${response.status}`,
        );
      }

      const payload =
        (await response.json()) as PatientWorkspaceOnboardingCommandResponseV1;

      if (
        payload.schemaVersion !==
          patientWorkspaceOnboardingCommandContractVersion ||
        payload.tenantId !== config.requestTenantId
      ) {
        throw new Error("patient-workspace-onboarding-command-schema-mismatch");
      }

      return payload;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError ?? new Error("patient-workspace-onboarding-command-failed");
}

export async function synthesizePatientWorkspaceSpeech(
  config: MobileRuntimeConfig,
  input: {
    patientId: string | null;
    bodyText: string;
    voiceId?: PatientWorkspaceSpeechVoiceIdV1 | null;
  },
  signal?: AbortSignal,
): Promise<{
  audioBlob: Blob;
  engine: PatientWorkspaceSpeechEngineV1 | null;
  voiceId: PatientWorkspaceSpeechVoiceIdV1 | null;
}> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return synthesizePatientWorkspaceScaffoldSpeech();
  }

  const request: PatientWorkspaceSpeechRequestV1 = {
    schemaVersion: patientWorkspaceSpeechContractVersion,
    tenantId: config.requestTenantId,
    patientId: input.patientId,
    bodyText: input.bodyText,
    voiceId: input.voiceId ?? null,
  };
  const response = await fetch(buildPatientWorkspaceSpeechUrl(config), {
    body: JSON.stringify(request),
    credentials: "include",
    headers: {
      accept: "audio/mpeg",
      "content-type": "application/json",
    },
    method: "POST",
    signal,
  });

  if (!response.ok) {
    throw new Error(`patient-workspace-speech-failed-${response.status}`);
  }

  const audioBlob = await response.blob();
  const voiceId = response.headers.get("x-amplecare-speech-voice-id");
  const engine = response.headers.get("x-amplecare-speech-engine");

  return {
    audioBlob,
    engine:
      engine === "generative" || engine === "neural"
        ? engine
        : null,
    voiceId:
      voiceId === "Danielle" ||
      voiceId === "Joanna" ||
      voiceId === "Ruth" ||
      voiceId === "Salli" ||
      voiceId === "Tiffany"
        ? voiceId
        : null,
  };
}

export async function submitSupportSubmission(
  config: MobileRuntimeConfig,
  input: Omit<SupportSubmissionRequestV1, "schemaVersion" | "tenantId">,
  signal?: AbortSignal,
): Promise<SupportSubmissionResponseV1> {
  if (isPatientWorkspaceScaffoldMode(config)) {
    return submitPatientWorkspaceScaffoldSupportSubmission(config, input);
  }

  const request: SupportSubmissionRequestV1 = {
    schemaVersion: supportSubmissionContractVersion,
    tenantId: config.requestTenantId,
    ...input,
  };
  const response = await fetch(buildSupportSubmissionUrl(config), {
    body: JSON.stringify(request),
    credentials: "include",
    headers: {
      accept: "application/json",
      "content-type": "application/json",
    },
    method: "POST",
    signal,
  });

  if (!response.ok) {
    throw new Error(`support-submission-failed-${response.status}`);
  }

  const payload = (await response.json()) as SupportSubmissionResponseV1;

  if (
    payload.schemaVersion !== supportSubmissionContractVersion ||
    payload.tenantId !== config.requestTenantId
  ) {
    throw new Error("support-submission-schema-mismatch");
  }

  return payload;
}
